import { faUserSecret } from "@fortawesome/pro-light-svg-icons";
import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons/faEyeSlash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Box, Button, CardContent, Grid, Stack, TextField } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcCard, MprcCardActions, MprcSpinnerIndicator } from "components";
import { ButtonCancel } from "components/buttons";
import { ExtractErrorInformation } from "components/MprcRequestErrorReport";
import { AccountLoginCheckResult, SpyLoginAuthDto } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useSnackbar } from 'notistack';
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from "react-router";
import { RootPaths } from "routes/RootRouter";
import { AuthService } from "services/authService";
import { SessionService } from "services/sessionService";
import { NIL } from 'uuid';
import { LocationState } from "views/public/LoginPage";

////////////////////////////////////////////////////////////////////////////////

export const SpyLoginPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { authorizationId = NIL } = useParams();
    const session = useSession();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const api = useCoreApi();


    const fromUrl = (location.state as LocationState)?.from?.pathname || "/";
    const returnUrl = fromUrl ?? RootPaths.Home;

    const [loading, setLoading] = useState<boolean>(true);
    const [submitting, setSubmit] = useState<boolean>(false);
    const [authResultMessage, setAuthResultMessage] = useState<string | null>(null);
    const [viewData, setViewData] = useState<SpyLoginAuthDto | null>(null);

    const [password, setPassword] = useState<string | undefined>(undefined);
    const [showPass, setShowPass] = useState<boolean>(false);
    const passwordInputRef = useRef<HTMLInputElement>(null);

    ////////////////////////////////////////////////////////////////////////////

    ///
    const load = async () => {
        setLoading(true);

        try {
            const response = await api.v1.user.getSpyLoginAuth({ authorizationId });
            setViewData(response.data);
        }
        catch (error) {
            const e = ExtractErrorInformation(error as ApiProblemDetails);
            setAuthResultMessage(e.message ?? null);
        }
        finally {
            setLoading(false);
        }
    };

    ///
    const handleSubmit = async () => {
        setSubmit(true);

        try {
            const result = await AuthService.spySignIn(authorizationId, password!);

            if (result.loginStatus == AccountLoginCheckResult.Valid) {
                session.createSession(result.jwt!);
                navigate(returnUrl, { replace: true });
            }
            else if (result.loginStatus != undefined) {
                setAuthResultMessage(result.errorMessage);
            }
            else {
                enqueueSnackbar(result.errorMessage);
            }
        }
        catch (e: any) {
            enqueueSnackbar(e.message, { variant: "error", preventDuplicate: true });
            navigate("/");
        }
        finally {
            setSubmit(false);
        }
    };

    ///
    const handlePasswordKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.keyCode == 13) {
            event.preventDefault();
            handleSubmit();
        }
    };

    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        load();
    }, []);

    ////////////////////////////////////////////////////////////////////////////

    const canAccess = viewData != null && viewData.approved == true && !viewData.expired;


    return (
        <Box sx={{ width: 450, margin: "0 auto" }} >
            <Grid container direction="column" justifyContent="center" alignItems="center" spacing={2} width="100%">
                <Grid width="100%">
                    <img
                        src={SessionService.current.appImages.highlightImageUrl}
                        width="360"
                    />

                    {
                        authResultMessage &&
                        <Alert severity="error" variant="filled" >
                            {authResultMessage}
                        </Alert>
                    }
                </Grid>

                <Grid width="100%">
                    {
                        loading
                            ? <MprcSpinnerIndicator />
                            : viewData != null &&
                            (
                                <MprcCard>
                                    <CardContent>

                                        <Stack direction="column" width="100%" spacing={1}>

                                            <Stack direction="row" width="100%" spacing={1}>
                                                <div>
                                                    <FontAwesomeIcon icon={faUserSecret} size="2x" />
                                                </div>
                                                <div>
                                                    {
                                                        viewData.approved !== true
                                                            ? <Alert severity="error">
                                                                {t("Auth.SpyLogin.WasNotAuthorizedText")}
                                                            </Alert>
                                                            : viewData.expired
                                                                ? <Alert severity="warning">
                                                                    {t("Auth.SpyLogin.ExpiredText")}
                                                                </Alert>
                                                                : <Trans i18nKey="Auth.SpyLogin.AuthorizeText"
                                                                    values={{
                                                                        name: viewData.user.name,
                                                                        date: Formaters.performaDate(viewData.validUntil)
                                                                    }} />
                                                    }
                                                </div>
                                            </Stack>

                                            {canAccess && <div><hr /></div>}

                                            {
                                                canAccess &&
                                                <div>
                                                    <TextField
                                                        id="performaId"
                                                        label={t("Auth.SpyLogin.AuthorizedUserLabel")}
                                                        fullWidth={true}
                                                        disabled={true}
                                                        value={viewData.authUser.name}
                                                    />

                                                    <TextField
                                                        id="pass"
                                                        name="password"
                                                        autoFocus
                                                        label={t("Auth.SpyLogin.PasswordLabel")}
                                                        type={showPass ? undefined : "password"}
                                                        autoComplete="performaPass"
                                                        error={password != null && password.length == 0}
                                                        margin="normal"
                                                        fullWidth={true}
                                                        disabled={submitting}
                                                        value={password || ""}
                                                        inputRef={passwordInputRef}
                                                        slotProps={{
                                                            input: {
                                                                inputProps: {
                                                                    maxLength: 32,
                                                                    onKeyDown: (e) => handlePasswordKeyDown(e)
                                                                },
                                                                endAdornment: <FontAwesomeIcon
                                                                    className={AppGlobalClassKeys.clickable}
                                                                    icon={showPass ? faEye : faEyeSlash}
                                                                    fixedWidth
                                                                    onClick={() => setShowPass(!showPass)}
                                                                />
                                                            }
                                                        }}
                                                        onChange={(e) => setPassword(e.target.value)}
                                                    />
                                                </div>
                                            }
                                        </Stack>

                                    </CardContent>

                                    <MprcCardActions>
                                        {
                                            canAccess &&
                                            <Button
                                                onClick={handleSubmit}
                                                disabled={submitting}
                                            >
                                                {t("Auth.SpyLogin.ButtonLoginText")}
                                                {submitting && <MprcSpinnerIndicator />}
                                            </Button>
                                        }
                                        <ButtonCancel
                                            onClick={() => navigate(RootPaths.Login)}
                                        />
                                    </MprcCardActions>
                                </MprcCard>
                            )
                    }
                </Grid>
            </Grid>
        </Box >
    );
};
